import React from 'react';
import styled from 'styled-components';
import type { TurnUserPrompt } from '../../../hooks/useWorkspace';

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const Author = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const AutoBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.0625rem 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  border-radius: 999px;
  background: var(--accent-subtle, rgba(99, 102, 241, 0.12));
  color: var(--accent, #6366f1);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Timestamp = styled.span`
  color: var(--text-muted);
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
`;

const PromptQuote = styled.div`
  border-left: 3px solid var(--accent, #6366f1);
  padding: 0.25rem 0 0.25rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.9375rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
`;

function formatTime(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

interface TurnPromptHeaderProps {
  userPrompt: TurnUserPrompt;
  turnIndex: number;
}

/**
 * Renders the "You said X" block at the top of a turn: author pill, optional
 * "auto" badge for synthetic input-response turns, timestamp, and the
 * quoted user prompt with an accent left border.
 */
const TurnPromptHeader: React.FC<TurnPromptHeaderProps> = ({
  userPrompt,
  turnIndex,
}) => {
  return (
    <Header aria-label={`Turn ${turnIndex + 1} prompt`}>
      <AuthorRow>
        <Author>You</Author>
        {userPrompt.autoBadge && <AutoBadge>Auto</AutoBadge>}
        <Timestamp>{formatTime(userPrompt.timestamp)}</Timestamp>
      </AuthorRow>
      <PromptQuote>{userPrompt.content}</PromptQuote>
    </Header>
  );
};

export default TurnPromptHeader;
